/*
 * Copyright (c) 2025 NoorWays. All rights reserved.
 * Licensed under the terms specified in the LICENSE.txt file.
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { createSubscriptionService } from '@/api/subscriptionService'
import type {
  SubscriptionPlan as ApiSubscriptionPlan,
  SubscriptionPlanDefinition,
  UserSubscriptionState,
  SubscriptionPlanType
} from '@/types/subscription'
import stripeService from '@/services/stripeService'
import appConfig from '@/config/appConfig'
import { PricingApi, Configuration, type PricingInfo } from '@/api/backend'
import { logger } from '@/services/logger'

// Re-export for backward compatibility
export type { UserSubscriptionState } from '@/types/subscription'

export const useSubscriptionStore = defineStore('subscription', () => {
  // Get i18n instance for translations
  const { t } = useI18n()

  // Dynamic pricing data from API
  const pricingData = ref<PricingInfo[]>([])
  const pricingLoading = ref(false)
  const pricingError = ref<string | null>(null)

  // Static plan definitions with translation keys (prices will be updated from API)
  const planDefinitions = ref<SubscriptionPlanDefinition[]>([
    {
      id: 'free',
      name: 'subscription.plans.free',
      price: 0,
      currency: 'EUR',
      interval: 'month',
      features: [
        'subscription.features.basicWriting',
        'subscription.features.fiveAiRequests',
      ],
    },
    {
      id: 'premium-monthly',
      name: 'subscription.plans.premiumMonthly',
      price: 0, // Will be updated from API
      currency: 'EUR',
      interval: 'month',
      features: [
        'subscription.features.unlimitedRequests',
        'subscription.features.advancedFeatures',
        'subscription.features.multiLanguageSupport',
        'subscription.features.prioritySupport',
      ],
    },
    {
      id: 'premium-yearly',
      name: 'subscription.plans.premiumYearly',
      price: 0, // Will be updated from API
      currency: 'EUR',
      interval: 'year',
      features: [
        'subscription.features.unlimitedRequests',
        'subscription.features.advancedFeatures',
        'subscription.features.multiLanguageSupport',
        'subscription.features.prioritySupport',
      ],
    },
  ])

  // Computed property for translated plans with dynamic pricing
  const plans = computed(() =>
    planDefinitions.value.map(plan => {
      // Find pricing data for this plan
      const pricingInfo = pricingData.value.find(p => {
        if (plan.id === 'premium-monthly') return p.planId === 'monthly'
        if (plan.id === 'premium-yearly') return p.planId === 'yearly'
        return false
      })

      return {
        ...plan,
        name: t(plan.name),
        features: plan.features.map(feature => t(feature)),
        price: pricingInfo?.price ?? plan.price,
        currency: pricingInfo?.currency ?? plan.currency,
        savings: pricingInfo?.savings,
        isRecommended: pricingInfo?.isRecommended ?? false
      }
    })
  )

  const isLoading = ref(false)
  const isCancelling = ref(false)
  const currentPlan = ref<SubscriptionPlanType>('free')
  const subscriptionState = ref<UserSubscriptionState>({
    plan: 'free',
    status: 'active',
    usageCount: 0,
    maxFreeUsage: appConfig.subscription.maxFreeUsage,
    isPremium: false,
    isCancelled: false,
    isExpired: false,
    expiresAt: null,
    cancelledAt: null,
    startedAt: null
  })

  // Getters
  const freePlan = computed(() => plans.value.find(p => p.id === 'free'))
  const premiumPlans = computed(() => plans.value.filter(p => p.id !== 'free'))
  const isPremium = computed(() => subscriptionState.value.isPremium)
  const usageCount = computed(() => subscriptionState.value.usageCount)
  const maxFreeUsage = computed(() => subscriptionState.value.maxFreeUsage)
  const canUseFeature = computed(() => subscriptionState.value.isPremium || subscriptionState.value.usageCount < subscriptionState.value.maxFreeUsage)
  const isCancelled = computed(() => subscriptionState.value.isCancelled || subscriptionState.value.status === 'cancelled')
  const isExpired = computed(() => subscriptionState.value.isExpired || subscriptionState.value.status === 'expired')
  const expiresAt = computed(() => subscriptionState.value.expiresAt)
  const cancelledAt = computed(() => subscriptionState.value.cancelledAt)
  const startedAt = computed(() => subscriptionState.value.startedAt)
  const subscriptionStatus = computed(() => subscriptionState.value.status || 'active')
  const isExpiringSoon = computed(() => {
    if (!subscriptionState.value.expiresAt) return false
    const now = new Date()
    const expiry = new Date(subscriptionState.value.expiresAt)
    const daysUntilExpiry = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
    return daysUntilExpiry <= 7 // Expiring within 7 days
  })

  // Actions


  /**
   * Load subscription data from backend
   */
  async function loadSubscription(): Promise<{ success: boolean; error?: string }> {
    isLoading.value = true

    try {
      const subscriptionService = createSubscriptionService()
      const subscription = await subscriptionService.getSubscription()

      subscriptionState.value = subscription
      currentPlan.value = subscription.plan

      // Store in localStorage for persistence
      localStorage.setItem('noorwriter-subscription', JSON.stringify({
        plan: subscription.plan,
        usageCount: subscription.usageCount,
        maxFreeUsage: subscription.maxFreeUsage,
        isPremium: subscription.isPremium,
        lastUpdated: Date.now()
      }))

      return { success: true }
    } catch (error) {
      logger.error('Failed to load subscription', error instanceof Error ? error : undefined, {}, 'Subscription')
      return { success: false, error: error instanceof Error ? error.message : 'Failed to load subscription' }
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Load pricing data from backend
   */
  async function loadPricing(): Promise<{ success: boolean; error?: string }> {
    pricingLoading.value = true
    pricingError.value = null

    try {
      // Create pricing API instance
      const config = new Configuration({
        basePath: appConfig.apiBaseUrl,
        baseOptions: {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        }
      })
      const pricingApi = new PricingApi(config)
      const response = await pricingApi.getPricing()

      if (response.data.success) {
        pricingData.value = response.data.plans || []

        // Store in localStorage for caching
        localStorage.setItem('noorwriter-pricing', JSON.stringify({
          plans: response.data.plans,
          lastUpdated: Date.now(),
          expiresAt: Date.now() + ((response.data.cacheExpirationSeconds || 3600) * 1000)
        }))

        return { success: true }
      } else {
        pricingError.value = response.data.error || 'Failed to load pricing'
        return { success: false, error: pricingError.value }
      }
    } catch (error) {
      logger.error('Failed to load pricing', error instanceof Error ? error : undefined, {}, 'Subscription')
      const errorMessage = error instanceof Error ? error.message : 'Failed to load pricing'
      pricingError.value = errorMessage
      return { success: false, error: errorMessage }
    } finally {
      pricingLoading.value = false
    }
  }

  /**
   * Load pricing from cache or fetch from API
   */
  async function initializePricing(): Promise<void> {
    // Try to load from cache first
    try {
      const cached = localStorage.getItem('noorwriter-pricing')
      if (cached) {
        const cachedData = JSON.parse(cached)
        const now = Date.now()

        // Check if cache is still valid
        if (cachedData.expiresAt && now < cachedData.expiresAt) {
          pricingData.value = cachedData.plans || []
          return
        }
      }
    } catch (error) {
      logger.warn('Failed to load cached pricing', { error: error instanceof Error ? error.message : String(error) }, 'Subscription')
    }

    // Cache is invalid or doesn't exist, fetch from API
    await loadPricing()
  }

  /**
   * Create Stripe checkout session and redirect to payment
   */
  async function upgradeToPremium(planId: string): Promise<{ success: boolean; error?: string; url?: string }> {
    isLoading.value = true

    try {
      const subscriptionService = createSubscriptionService()
      const planType = planId.includes('yearly') ? 'yearly' : 'monthly'

      const result = await subscriptionService.createCheckoutSession(planType as ApiSubscriptionPlan)

      if (result.success && result.sessionId) {
        // Redirect to Stripe checkout
        const stripeResult = await stripeService.redirectToCheckout(result.sessionId)

        if (!stripeResult.success) {
          return { success: false, error: stripeResult.error }
        }

        return { success: true, url: result.url }
      } else {
        return { success: false, error: result.error || 'Failed to create checkout session' }
      }
    } catch (error) {
      logger.error('Failed to upgrade to premium', error instanceof Error ? error : undefined, {}, 'Subscription')
      return { success: false, error: error instanceof Error ? error.message : 'Payment failed' }
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Cancel the user's premium subscription
   */
  async function cancelSubscription(): Promise<{ success: boolean; error?: string; expiresAt?: Date; message?: string }> {
    isCancelling.value = true
    try {
      const subscriptionService = createSubscriptionService()
      const result = await subscriptionService.cancelSubscription()

      if (result.success) {
        // Update subscription state to reflect cancellation
        subscriptionState.value = {
          ...subscriptionState.value,
          status: 'cancelled',
          isCancelled: true,
          expiresAt: result.expiresAt || null,
          cancelledAt: new Date()
        }

        // Update localStorage
        localStorage.setItem('noorwriter-subscription', JSON.stringify({
          ...subscriptionState.value,
          lastUpdated: Date.now()
        }))

        return {
          success: true,
          expiresAt: result.expiresAt,
          message: result.message
        }
      } else {
        return { success: false, error: result.error || 'Failed to cancel subscription' }
      }
    } catch (error) {
      logger.error('Failed to cancel subscription', error instanceof Error ? error : undefined, {}, 'Subscription')
      return { success: false, error: error instanceof Error ? error.message : 'Cancellation failed' }
    } finally {
      isCancelling.value = false
    }
  }

  /**
   * Force refresh subscription data from backend (bypasses cache)
   */
  async function refreshSubscription(): Promise<{ success: boolean; error?: string }> {
    // Clear cache first
    localStorage.removeItem('noorwriter-subscription')

    // Load fresh data from backend
    return await loadSubscription()
  }

  /**
   * Initialize subscription from localStorage or load from backend
   */
  async function initializeSubscription(forceRefresh: boolean = false): Promise<void> {
    try {
      // If force refresh is requested, skip cache
      if (!forceRefresh) {
        // Try to load from localStorage first
        const saved = localStorage.getItem('noorwriter-subscription')
        if (saved) {
          const data = JSON.parse(saved)
          const lastUpdated = data.lastUpdated || 0
          const isRecent = Date.now() - lastUpdated < 5 * 60 * 1000 // 5 minutes

          if (isRecent) {
            subscriptionState.value = {
              plan: data.plan || 'free',
              status: data.status || 'active',
              usageCount: data.usageCount || 0,
              maxFreeUsage: data.maxFreeUsage || appConfig.subscription.maxFreeUsage,
              isPremium: data.isPremium || false,
              isCancelled: data.isCancelled || false,
              isExpired: data.isExpired || false,
              expiresAt: data.expiresAt ? new Date(data.expiresAt) : null,
              cancelledAt: data.cancelledAt ? new Date(data.cancelledAt) : null,
              startedAt: data.startedAt ? new Date(data.startedAt) : null
            }
            currentPlan.value = data.plan || 'free'
          }
        }
      }

      // Refresh from backend
      await loadSubscription()
    } catch {
      // Fallback to free plan
      subscriptionState.value = {
        plan: 'free',
        status: 'active',
        usageCount: 0,
        maxFreeUsage: appConfig.subscription.maxFreeUsage,
        isPremium: false,
        isCancelled: false,
        isExpired: false,
        expiresAt: null,
        cancelledAt: null,
        startedAt: null
      }
      currentPlan.value = 'free'
    }
  }

  /**
   * Increment usage count for free users
   */
  async function incrementUsage(): Promise<boolean> {
    if (subscriptionState.value.isPremium) {
      return true // Premium users have unlimited usage
    }

    if (subscriptionState.value.usageCount >= subscriptionState.value.maxFreeUsage) {
      return false // Usage limit reached
    }

    subscriptionState.value.usageCount++

    // Update localStorage
    localStorage.setItem('noorwriter-subscription', JSON.stringify({
      ...subscriptionState.value,
      lastUpdated: Date.now()
    }))


    return true
  }

  /**
   * Clear subscription data (used during logout)
   */
  function clearSubscriptionData() {
    // Reset to free plan state
    subscriptionState.value = {
      plan: 'free',
      status: 'active',
      usageCount: 0,
      maxFreeUsage: appConfig.subscription.maxFreeUsage,
      isPremium: false,
      isCancelled: false,
      isExpired: false,
      expiresAt: null,
      cancelledAt: null,
      startedAt: null
    }
    currentPlan.value = 'free'

    // Clear localStorage
    localStorage.removeItem('noorwriter-subscription')
  }

  return {
    // State
    plans,
    isLoading,
    isCancelling,
    currentPlan,
    subscriptionState,
    pricingData,
    pricingLoading,
    pricingError,
    // Getters
    freePlan,
    premiumPlans,
    isPremium,
    usageCount,
    maxFreeUsage,
    canUseFeature,
    isCancelled,
    isExpired,
    expiresAt,
    cancelledAt,
    startedAt,
    subscriptionStatus,
    isExpiringSoon,

    // Actions
    loadSubscription,
    loadPricing,
    initializePricing,
    refreshSubscription,
    upgradeToPremium,
    cancelSubscription,
    initializeSubscription,
    incrementUsage,
    clearSubscriptionData,
  }
})
